import { Text, TextLink, TextProps } from '@cinch-labs/design-system'
import { Env, readFromEnv } from '@cinch-nx/environments'
import cx from 'classnames'
import Image from 'next/legacy/image'
import { useEffect, useState } from 'react'

import CinchLogo from './assets/cinch-logo.svg'

import { BrandSection } from './brand-section/brand-section'
import { Compliance } from './compliance/compliance'
import { Copyright } from './copyright/copyright'
import {
  companyLinks,
  footerLinks,
  legalLinks,
  siteNavigationLinks,
  socialLinks,
  socialMediaLinks,
  socialNetworkButtons,
  usefulLinks,
} from './data/index'

import FooterLinksList from './footer-links-list/footer-links-list'
import HelpAndSupportCta from './help-and-support-cta/help-and-support-cta'
import HelpAndSupport from './help-and-support/help-and-support'
import styles from './shared-ui-footer.module.css'
import SocialLinkList from './social-link-list/social-link-list'
import SocialMediaLinkList from './social-media-link-list/social-media-link-list'
import { SocialNetworkButton } from './social-network-button/social-network-button'
import { FooterLink } from './types'
import { AppNudge } from './app-nudge/app-nudge'
import { trackFooterLinkClick } from './tracking'

const MarshallMotorGroupUrl = 'https://www.marshall.co.uk'

type TextLinksListProps = {
  className?: string
  links: (FooterLink & { textProps?: TextProps })[]
}

const TextLinksList = ({ className, links }: TextLinksListProps) => (
  <ul className={className}>
    {links.map(({ label, href, textProps }) => (
      <li key={'footer-link-' + label}>
        <TextLink
          href={href}
          target={href.startsWith('http') ? '_blank' : undefined}
          isOnlyWhite
          hasNoLine
          textProps={textProps}
          onClick={() => trackFooterLinkClick(href, label)}
        >
          {label}
        </TextLink>
      </li>
    ))}
  </ul>
)

export function SharedUiFooter({
  displayNewFooter = false,
  showFooterBorderRadius = true,
}) {
  const [year, setYear] = useState<number | null>(null)

  useEffect(() => {
    setYear(new Date().getFullYear())
  }, [])

  const ShowHelpAndSupportChangesFeatureFlag =
    readFromEnv(Env.ShowHelpAndSupportChanges) === 'true'

  if (displayNewFooter) {
    return (
      <>
        <div className={styles.appNudgeWrapper}>
          <AppNudge
            title="Get the cinch app"
            body="Browse quality new and used cars, book a service and track your key dates so you never miss an MOT again."
            image={
              <img
                src={
                  'https://a.storyblok.com/f/132543/485x333/d24e60a24e/app-nudge-banner.png'
                }
                alt="Cinch app preview"
                className={styles.bannerImage}
                loading="lazy"
              />
            }
          />
        </div>
        <footer
          className={cx(styles.newFooter, {
            [styles.noBorderRadius]: !showFooterBorderRadius,
          })}
          id="main-footer"
        >
          <div className={styles.newFooterContainer}>
            <div className={styles.brand}>
              <BrandSection
                logo={
                  <a href="/" aria-label="footer cinch">
                    <CinchLogo />
                  </a>
                }
                text="The biggest range of cars to buy entirely online"
                socialNetworkButtons={socialNetworkButtons.map((props) => (
                  <SocialNetworkButton {...props} />
                ))}
              />
            </div>
            <hr className={styles.divider} />
            <div className={styles.serviceNavigation}>
              <TextLinksList
                className={styles.siteNavigation}
                links={siteNavigationLinks.map((link) => ({
                  ...link,
                  textProps: {
                    fontSize: 'm',
                    fontWeight: 'bold',
                  },
                }))}
              />
            </div>
            <div className={styles.usefulLinks}>
              <Text color="pureWhite" fontSize="m" fontWeight="bold">
                Useful links
              </Text>
              <TextLinksList
                links={usefulLinks.map((link) => ({
                  ...link,
                  textProps: {
                    fontSize: 'xs',
                  },
                }))}
              />
            </div>
            <div className={styles.companyLinks}>
              <Text color="pureWhite" fontSize="m" fontWeight="bold">
                Company
              </Text>
              <TextLinksList
                links={companyLinks.map((link) => ({
                  ...link,
                  textProps: {
                    fontSize: 'xs',
                  },
                }))}
              />
            </div>
            <div className={styles.help}>
              <HelpAndSupportCta />
            </div>
            <div className={styles.compliance}>
              <Compliance />
            </div>

            <div className={styles.legalContainer}>
              <hr className={styles.legalDivider} />
              <div className={styles.copyright}>
                <Copyright />
              </div>
              <div className={styles.legalNavigation}>
                <TextLinksList
                  links={legalLinks.map((link) => ({
                    ...link,
                    textProps: {
                      fontSize: 'xs',
                    },
                  }))}
                />
              </div>
            </div>
          </div>
        </footer>
      </>
    )
  }

  return (
    <footer
      className={cx(styles.footer, {
        [styles.noBorderRadius]: !showFooterBorderRadius,
      })}
      id="main-footer"
    >
      <div className={styles.footerContainer}>
        {ShowHelpAndSupportChangesFeatureFlag ? (
          <HelpAndSupport />
        ) : (
          <>
            <Text
              element="h2"
              className={styles.heading}
              fontSize="m"
              lineHeight="body"
              fontWeight="bold"
            >
              Need a hand?
              <Text
                element="span"
                className={styles.subHeading}
                fontSize="s"
                lineHeight="body"
              >
                Call, email or WhatsApp, we’re here to help.
              </Text>
            </Text>
            <SocialLinkList links={socialLinks} />
          </>
        )}

        <div className={styles.appStoreButtons}>
          <a
            href={readFromEnv(Env.IOSAppStoreUrl)}
            aria-label="app store badge"
          >
            <Image
              src={
                'https://a.storyblok.com/f/132543/384x114/7266ad02b3/download-app-store.png'
              }
              height={48}
              width={164}
              alt="Download on the app store"
            />
          </a>
          <a
            href={readFromEnv(Env.AndroidAppStoreUrl)}
            aria-label="google play badge"
          >
            <Image
              src={
                'https://a.storyblok.com/f/132543/564x168/9d227fc037/google-play-badge1.png'
              }
              height={48}
              width={164}
              alt="Get it on google play"
            />
          </a>
        </div>

        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="bold"
          lineHeight="body"
        >
          Used cars are sold by cinch cars limited.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          Cinch Cars Limited is an appointed representative of{' '}
          <TextLink
            className={styles.itcLink}
            href={MarshallMotorGroupUrl}
            isVariant
          >
            Marshall&nbsp;Motor&nbsp;Group
          </TextLink>{' '}
          which is authorised and regulated by the Financial Conduct Authority
          (their registration number is 310503). Permitted activities include
          advising on and arranging general insurance contracts and acting as a
          credit broker not a lender.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          We can introduce you to a limited number of finance providers. We do
          not charge a fee for our Consumer Credit services. We do not act as a
          financial adviser, or fiduciary. We act in our own interest, whichever
          lender we introduce you to, we will typically receive commission from
          them based on either a fixed fee or a fixed percentage of the amount
          you borrow. Any and all commission amounts will be fully disclosed to
          you as part of your sales journey. You will be required to give your
          fully informed consent to our receipt of this commission. By doing
          this, you acknowledge that you understand our role as a credit broker,
          and that we will receive a financial incentive if you take out a loan
          from a lender that we introduce you to.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          All finance applications are subject to status, terms and conditions
          apply, UK residents only, 18&apos;s or over, Guarantees may be
          required.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="bold"
          lineHeight="body"
        >
          New cars are sold by Marshall Motor Group, who will also arrange any
          finance for those cars.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          &copy; {year || ''} Marshall Motor Group
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          Registered Office c/o Marshall Volkswagen Milton Keynes, Greyfriars
          Court, Milton Keynes, Buckinghamshire, MK10 0BN.
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          Registered in England Number 00295579 | VAT Reg. No: 213209019
        </Text>
        <Text
          element="p"
          className={styles.body}
          fontSize="s"
          fontWeight="light"
          lineHeight="body"
        >
          Marshall Motor Group Limited is authorised and regulated by the
          Financial Conduct Authority. Our Firm Reference Number is 310503.
          Details of our permissions and trading names can be checked via the
          FCA register{' '}
          <TextLink
            className={styles.itcLink}
            href="https://register.fca.org.uk/s/"
            isVariant
          >
            https://register.fca.org.uk/s/
          </TextLink>
        </Text>

        <FooterLinksList links={footerLinks} />

        <SocialMediaLinkList links={socialMediaLinks} />
      </div>
    </footer>
  )
}

export default SharedUiFooter
